import React from "react";
import styled from "styled-components";
import { Box, Flex } from "rebass/styled-components";

import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { SliderStep } from "MVVM/Hooks/useClipQualityConfigs";

type SliderVariant = "modal" | "overlay";

interface QualitySliderProps {
  label: string;
  labelExtra?: React.ReactNode;
  steps: SliderStep[];
  value: number;
  onChange: (idx: number) => void;
  onRelease: () => void;
  isStepAvailableFn: (value: string | number) => boolean;
  hasAnyUnavailable?: boolean;
  variant?: SliderVariant;
  renderStepExtra?: (step: SliderStep) => React.ReactNode;
}

export const QualitySlider = ({
  label,
  labelExtra,
  steps,
  value,
  onChange,
  onRelease,
  isStepAvailableFn,
  hasAnyUnavailable = false,
  variant = "modal",
  renderStepExtra,
}: QualitySliderProps) => {
  if (!steps.length) return null;

  return (
    <SliderContainer>
      <SliderLabel $variant={variant}>
        {label}
        {labelExtra}
      </SliderLabel>
      <SliderRight>
        <LabelsRow>
          {steps.map((step, i) => {
            const available = isStepAvailableFn(step.value);
            const isActive = i === value;
            const greyed = hasAnyUnavailable ? !isActive : !available;
            return (
              <StepLabel
                key={i}
                $greyed={greyed}
                $active={isActive}
                $variant={variant}
              >
                <StepLabelText>
                  {step.hoverText ? (
                    <AnimatedTooltip content={step.hoverText} position="top">
                      {step.label}
                    </AnimatedTooltip>
                  ) : (
                    step.label
                  )}
                </StepLabelText>
                <StepTagSlot>{renderStepExtra?.(step)}</StepTagSlot>
              </StepLabel>
            );
          })}
        </LabelsRow>
        <SliderTrack
          $variant={variant}
          style={{ padding: `0 calc(50% / ${steps.length} - 9px)` }}
        >
          <SliderInput
            as="input"
            type="range"
            min={0}
            max={steps.length - 1}
            step={1}
            value={value}
            $variant={variant}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange(Number(e.target.value))
            }
            onMouseUp={onRelease}
            onTouchEnd={onRelease}
          />
        </SliderTrack>
      </SliderRight>
    </SliderContainer>
  );
};

const SliderContainer = styled(Flex)`
  align-items: center;
  gap: 12px;
`;

const SliderLabel = styled(Flex)<{ $variant: SliderVariant }>`
  align-items: center;
  color: ${({ $variant, theme }) =>
    $variant === "overlay" ? "white" : theme.colors.ice};
  flex-shrink: 0;
  font-size: ${({ $variant }) => ($variant === "overlay" ? "14px" : "16px")};
  font-weight: 600;
  gap: 4px;
  width: ${({ $variant }) => ($variant === "overlay" ? "100px" : "110px")};
`;

const SliderRight = styled(Flex)`
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const LabelsRow = styled(Flex)`
  justify-content: space-between;
`;

const StepLabel = styled(Flex)<{
  $greyed: boolean;
  $active: boolean;
  $variant: SliderVariant;
}>`
  align-items: center;
  color: ${({ $greyed, $active, $variant, theme }) =>
    $variant === "overlay"
      ? $active
        ? "#4ebd94"
        : $greyed
        ? "rgba(255, 255, 255, 0.25)"
        : "rgba(255, 255, 255, 0.7)"
      : $active
      ? theme.colors.envy
      : $greyed
      ? theme.colors.chalkAlpha20
      : theme.colors.chalk};
  flex: 1;
  flex-direction: column;
  font-size: ${({ $variant }) => ($variant === "overlay" ? "13px" : "14px")};
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  gap: 3px;
  justify-content: center;
  min-width: 0;
  text-align: center;
  transition: color 0.15s;
`;

const StepLabelText = styled(Box)``;

const StepTagSlot = styled(Box)`
  height: 18px;
`;

const SliderTrack = styled(Flex)<{ $variant: SliderVariant }>`
  position: relative;
  width: 100%;

  /* full-width groove drawn here so the visible track length is identical
     regardless of step count; the input only spans the thumb-stop range */
  &::before {
    background: ${({ $variant, theme }) =>
      $variant === "overlay"
        ? "rgba(255, 255, 255, 0.2)"
        : theme.colors.chalkAlpha20};
    border-radius: 3px;
    content: "";
    height: 6px;
    left: 0;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const SliderInput = styled(Box)<{ $variant: SliderVariant }>`
  appearance: none;
  background: transparent;
  border-radius: 3px;
  cursor: pointer;
  height: 6px;
  outline: none;
  position: relative;
  width: 100%;

  &::-webkit-slider-thumb {
    appearance: none;
    background: ${({ $variant, theme }) =>
      $variant === "overlay" ? "#4ebd94" : theme.colors.envy};
    border: 2px solid
      ${({ $variant, theme }) =>
        $variant === "overlay" ? "rgba(0, 0, 0, 0.5)" : theme.colors.ned};
    border-radius: 50%;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    cursor: pointer;
    height: 18px;
    width: 18px;
  }

  &::-moz-range-track {
    background: transparent;
  }

  &::-moz-range-thumb {
    background: ${({ $variant, theme }) =>
      $variant === "overlay" ? "#4ebd94" : theme.colors.envy};
    border: 2px solid
      ${({ $variant, theme }) =>
        $variant === "overlay" ? "rgba(0, 0, 0, 0.5)" : theme.colors.ned};
    border-radius: 50%;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    cursor: pointer;
    height: 18px;
    width: 18px;
  }
`;
